import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationsAPI } from '../../api';
import { formatRelativeTime } from '../../utils';
import {
  Bell,
  BellOff,
  Mail,
  Award,
  TrendingUp,
  Target,
  Gift,
  Users2,
  MessageCircle,
  AtSign,
  Info,
  CheckCheck,
  Inbox,
  Loader2,
  AlertCircle,
  RefreshCw,
  Check,
  Trophy,
  Star,
  Sparkles,
} from 'lucide-react';

/**
 * NotificationsPage — Performance Club dark theme.
 *
 * Two tabs:
 *   - Feed (default): the user's notification stream with All / Unread
 *     filter, "Mark all as read", paginated "Load more", click-to-open
 *     with optimistic markRead.
 *   - Preferences: the email opt-in toggles for every supported
 *     notification type, grouped by category.
 *
 * Both tabs share the same shell. The active tab is local state
 * and reads `?tab=` from the URL on mount so the profile-menu
 * deep-link (`/notifications?tab=preferences`) lands on the right
 * tab. Tabs are also pushed to the URL via `replaceState` so a
 * manual switch updates the back/forward history.
 */
export const NotificationsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const fromQuery = searchParams.get('tab');
    if (fromQuery === 'preferences' || fromQuery === 'feed') return fromQuery;
    return 'feed';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  const setTab = useCallback((next) => {
    setActiveTab(next);
    setSearchParams((p) => {
      const np = new URLSearchParams(p);
      if (next === 'feed') np.delete('tab');
      else np.set('tab', next);
      return np;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <span className="hero-kicker">
          {t('notificationsPage.kicker', { defaultValue: 'Notifications' })}
        </span>
        <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
          {t('notificationsPage.title', { defaultValue: 'Your inbox' })}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t('notificationsPage.subtitle', {
            defaultValue: 'Stay on top of sessions, badges, and team activity.',
          })}
        </p>
      </div>

      <div
        className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-[rgba(255,255,255,0.04)]"
        role="tablist"
      >
        <TabButton
          id="feed"
          active={activeTab === 'feed'}
          onClick={() => setTab('feed')}
        >
          {t('notificationsPage.tabFeed', { defaultValue: 'Feed' })}
        </TabButton>
        <TabButton
          id="preferences"
          active={activeTab === 'preferences'}
          onClick={() => setTab('preferences')}
        >
          {t('notificationsPage.tabPreferences', { defaultValue: 'Preferences' })}
        </TabButton>
      </div>

      {activeTab === 'feed' ? <FeedTab /> : <PreferencesTab />}
    </div>
  );
};

function TabButton({ id, active, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
        active
          ? 'bg-primary-300 text-[#0a1208]'
          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
      }`}
      data-testid={`tab-${id}`}
    >
      {children}
    </button>
  );
}

// --------------------------------------------------------------------------
// Feed tab
// --------------------------------------------------------------------------
function FeedTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [skip, setSkip] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const inFlight = useRef(false);

  const PAGE_SIZE = 50;

  const fetchPage = useCallback(
    async (nextSkip, replace = false) => {
      if (inFlight.current) return;
      inFlight.current = true;
      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError('');
      try {
        const params = { limit: PAGE_SIZE, skip: nextSkip, unread_only: filter === 'unread' };
        const res = await notificationsAPI.list(params);
        const newItems = Array.isArray(res.data?.items) ? res.data.items : [];
        setItems((prev) => (replace ? newItems : [...prev, ...newItems]));
        setTotal(res.data?.total ?? newItems.length);
        setUnreadCount(res.data?.unread_count ?? 0);
        setSkip(nextSkip + newItems.length);
      } catch (err) {
        console.error('Failed to load notifications:', err);
        setError(
          t('notificationsPage.feed.error', {
            defaultValue: 'Could not load notifications.',
          })
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
        inFlight.current = false;
      }
    },
    [filter, t]
  );

  // Initial + filter change: refetch from zero.
  useEffect(() => {
    fetchPage(0, true);
  }, [fetchPage]);

  // Poll every 10s while on the feed.
  useEffect(() => {
    const interval = setInterval(() => {
      // Refresh first page only (lightweight refetch).
      if (!inFlight.current) fetchPage(0, true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchPage]);

  const handleClickItem = async (n) => {
    if (markingId) return;
    if (!n.is_read) {
      // Optimistic update.
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      setMarkingId(n.id);
      try {
        await notificationsAPI.markRead(n.id);
      } catch (err) {
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: false } : x)));
        setUnreadCount((c) => c + 1);
        console.error('Failed to mark notification read:', err);
      } finally {
        setMarkingId(null);
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    if (markingAll || unreadCount === 0) return;
    setMarkingAll(true);
    // Optimistic.
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    const prevUnread = unreadCount;
    setUnreadCount(0);
    try {
      await notificationsAPI.markAllRead();
    } catch (err) {
      // Roll back.
      setUnreadCount(prevUnread);
      // Re-fetch first page to restore the unread state.
      fetchPage(0, true);
      console.error('Failed to mark all read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleLoadMore = () => {
    if (loadingMore || items.length >= total) return;
    fetchPage(skip, false);
  };

  return (
    <div className="space-y-4" data-testid="notifications-feed">
      {/* Header row: filter + mark-all-read */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-[rgba(255,255,255,0.04)]"
          role="tablist"
        >
          <FilterButton
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            testid="filter-all"
          >
            {t('notificationsPage.feed.filterAll', { defaultValue: 'All' })}
          </FilterButton>
          <FilterButton
            active={filter === 'unread'}
            onClick={() => setFilter('unread')}
            testid="filter-unread"
          >
            {t('notificationsPage.feed.filterUnread', { defaultValue: 'Unread' })}
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-300 text-[#0a1208] text-[10px] font-semibold">
                {unreadCount}
              </span>
            )}
          </FilterButton>
        </div>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAll}
            disabled={markingAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            data-testid="mark-all-read"
          >
            {markingAll ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCheck className="w-4 h-4 text-primary-100" />
            )}
            {t('notificationsPage.feed.markAllRead', { defaultValue: 'Mark all as read' })}
          </button>
        )}
      </div>

      {loading ? (
        <FeedSkeleton />
      ) : error ? (
        <ErrorState
          message={error}
          onRetry={() => fetchPage(0, true)}
          t={t}
        />
      ) : items.length === 0 ? (
        <EmptyFeed
          filter={filter}
          t={t}
        />
      ) : (
        <>
          <ul className="space-y-3">
            {items.map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                onClick={() => handleClickItem(n)}
              />
            ))}
          </ul>

          {items.length < total && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
                data-testid="load-more"
              >
                {loadingMore ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 text-primary-100" />
                )}
                {t('notificationsPage.feed.loadMore', { defaultValue: 'Load more' })}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterButton({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors flex items-center ${
        active
          ? 'bg-primary-300 text-[#0a1208]'
          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function NotificationCard({ notification, onClick }) {
  const { t } = useTranslation();
  const meta = TYPE_META[notification.type] || TYPE_META.system;
  const Icon = meta.icon;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`group w-full text-left rounded-2xl border transition-colors relative overflow-hidden ${
          notification.is_read
            ? 'border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] hover:bg-white/[0.04]'
            : 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] hover:bg-[rgba(213,255,123,0.08)]'
        }`}
        data-testid={`notification-card-${notification.id}`}
      >
        {!notification.is_read && (
          <span
            className="absolute left-0 top-0 bottom-0 w-1 bg-primary-300"
            aria-hidden="true"
          />
        )}
        <div className="flex gap-3 p-4">
          <div
            className={`shrink-0 h-10 w-10 rounded-xl border flex items-center justify-center ${
              notification.is_read
                ? 'border-[var(--color-border)] bg-white/[0.04]'
                : 'border-[rgba(213,255,123,0.25)] bg-[rgba(213,255,123,0.08)]'
            }`}
          >
            <Icon className="w-5 h-5 text-primary-100" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <p
                className={`text-sm truncate ${
                  notification.is_read
                    ? 'text-[var(--color-text-muted)]'
                    : 'text-[var(--color-text)] font-semibold'
                }`}
              >
                {notification.title}
              </p>
              <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {formatRelativeTime(notification.created_at)}
              </span>
            </div>
            <p className="mt-1 text-sm text-[var(--color-text-muted)] line-clamp-3">
              {notification.body}
            </p>
            {notification.link && (
              <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-primary-200">
                {t('notificationsPage.feed.openLink', { defaultValue: 'Open' })} →
              </p>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function FeedSkeleton() {
  return (
    <ul className="space-y-3" data-testid="notifications-feed-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <li
          key={i}
          className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4 flex gap-3"
        >
          <div className="h-10 w-10 rounded-xl bg-white/[0.05] animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-white/[0.05] animate-pulse" />
            <div className="h-3 w-2/3 rounded bg-white/[0.05] animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-white/[0.05] animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyFeed({ filter, t }) {
  const isFiltered = filter === 'unread';
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] py-16 text-center"
      data-testid="notifications-feed-empty"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-4">
        <Inbox className="w-7 h-7 text-[var(--color-text-muted)]" />
      </div>
      <p className="font-display text-xl text-[var(--color-text)]">
        {isFiltered
          ? t('notificationsPage.feed.emptyUnreadTitle', { defaultValue: "You're all caught up" })
          : t('notificationsPage.feed.emptyTitle', { defaultValue: 'No notifications yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)]">
        {isFiltered
          ? t('notificationsPage.feed.emptyUnreadHint', {
              defaultValue: 'No unread items right now.',
            })
          : t('notificationsPage.feed.emptyHint', {
              defaultValue: "We'll let you know when something happens.",
            })}
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div
      className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-4 flex items-center gap-3"
      data-testid="notifications-feed-error"
    >
      <AlertCircle className="w-5 h-5 shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-xs font-semibold hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('notificationsPage.feed.retry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------
// Preferences tab
// --------------------------------------------------------------------------
// All 11 toggleable email preferences, grouped by category. In-app + SSE
// channels are always on; only the email opt-in is mutable.
const PREF_GROUPS = [
  {
    id: 'sessions',
    label: 'Sessions',
    icon: Trophy,
    items: [
      { key: 'email_session_logged', label: 'Session Logged', description: 'When a trainer logs a session for you' },
      { key: 'email_session_confirmed', label: 'Session Confirmed', description: 'When you confirm a session' },
    ],
  },
  {
    id: 'achievements',
    label: 'Achievements',
    icon: Award,
    items: [
      { key: 'email_badge_earned', label: 'Badge Earned', description: 'When you earn a new badge' },
      { key: 'email_level_up', label: 'Level Up', description: 'When you reach a new level' },
      { key: 'email_challenge_completed', label: 'Challenge Completed', description: 'When you complete a challenge' },
      { key: 'email_reward_redeemed', label: 'Reward Redeemed', description: 'When you redeem a reward' },
    ],
  },
  {
    id: 'social',
    label: 'Social',
    icon: MessageCircle,
    items: [
      { key: 'email_message_received', label: 'Message Received', description: 'When someone sends you a direct message' },
      { key: 'email_team_invite', label: 'Team Invite', description: 'When you receive a team invitation' },
      { key: 'email_mention', label: 'Mention', description: 'When someone @mentions you in a team chat' },
    ],
  },
  {
    id: 'system',
    label: 'System & admin',
    icon: Info,
    items: [
      { key: 'email_system', label: 'System Updates', description: 'Planned maintenance, new features, and other announcements' },
      { key: 'email_contact_form_received', label: 'Contact Form Reply', description: 'Reply notifications for support requests submitted to GymGamify' },
    ],
  },
];

function PreferencesTab() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const response = await notificationsAPI.getPreferences();
        setPrefs(response.data);
      } catch (err) {
        console.error('Failed to load preferences:', err);
        setError(t('notificationsPage.preferences.errorLoad', { defaultValue: 'Could not load preferences.' }));
      } finally {
        setLoading(false);
      }
    };
    loadPrefs();
  }, [t]);

  const handleToggle = async (key) => {
    if (!prefs) return;
    const newValue = !prefs[key];
    setPrefs({ ...prefs, [key]: newValue });
    try {
      await notificationsAPI.updatePreferences({ [key]: newValue });
    } catch (err) {
      console.error('Failed to update preference:', err);
      setPrefs({ ...prefs, [key]: !newValue });
      // eslint-disable-next-line no-alert
      alert(t('notificationsPage.preferences.alertSave', { defaultValue: 'Failed to update preference' }));
    }
  };

  const allKeys = PREF_GROUPS.flatMap((g) => g.items.map((i) => i.key));
  const enabledCount = prefs ? allKeys.filter((k) => prefs[k]).length : 0;
  const totalCount = allKeys.length;

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="notifications-preferences-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary-200" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3" data-testid="notifications-preferences-error">
        <AlertCircle className="w-5 h-5 text-red-300 shrink-0" />
        <p className="text-sm text-red-100">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-preferences">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl uppercase text-[var(--color-text)]">
            {t('notificationsPage.preferences.title', { defaultValue: 'Email preferences' })}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('notificationsPage.preferences.subtitle', {
              defaultValue: 'Choose which updates land in your inbox.',
            })}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] text-xs">
          <Mail className="w-3.5 h-3.5 text-primary-100" />
          <span className="font-mono text-[var(--color-text)]">
            {enabledCount} / {totalCount}
          </span>
          <span className="text-[var(--color-text-muted)]">enabled</span>
        </div>
      </div>

      <div className="space-y-4">
        {PREF_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div
              key={group.id}
              className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] overflow-hidden"
              data-testid={`pref-group-${group.id}`}
            >
              <div className="px-4 py-3 flex items-center gap-2.5 border-b border-[var(--color-border)] bg-white/[0.02]">
                <div className="p-1.5 rounded-lg border border-[var(--color-border)] bg-white/[0.04]">
                  <GroupIcon className="w-3.5 h-3.5 text-primary-100" />
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text)]">
                  {group.label}
                </h3>
              </div>
              <ul className="divide-y divide-[var(--color-border)]">
                {group.items.map(({ key, label, description }) => {
                  const checked = prefs?.[key] ?? true;
                  return (
                    <li
                      key={key}
                      className="p-4 flex items-center justify-between gap-4"
                      data-testid={`pref-row-${key}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--color-text)]">{label}</p>
                        <p className="text-sm text-[var(--color-text-muted)]">{description}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={checked}
                        aria-label={label}
                        onClick={() => handleToggle(key)}
                        className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-200/40 ${
                          checked ? 'bg-primary-300' : 'bg-white/[0.08]'
                        }`}
                        data-testid={`pref-switch-${key}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 rounded-full bg-[#0a1208] transform transition-transform ${
                            checked ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        >
                          {checked && (
                            <Check className="w-3.5 h-3.5 text-primary-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4 flex items-start gap-3">
        <div className="p-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] shrink-0">
          <Bell className="w-5 h-5 text-primary-100" />
        </div>
        <div className="text-sm text-[var(--color-text-muted)] space-y-1">
          <p>
            {t('notificationsPage.preferences.emailNote', {
              defaultValue:
                'Email notifications are sent to your registered email address. In-app and bell alerts are always on — these toggles only mute the email copy.',
            })}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]/70">
            {t('notificationsPage.preferences.emailNote2', {
              defaultValue: 'Tip: bookmark /notifications?tab=preferences to come back here quickly.',
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

// Shared type → icon map (also used by NotificationBell).
const TYPE_META = {
  session_logged: { icon: Mail },
  session_confirmed: { icon: Mail },
  badge_earned: { icon: Award },
  level_up: { icon: TrendingUp },
  challenge_completed: { icon: Target },
  reward_redeemed: { icon: Gift },
  team_invite: { icon: Users2 },
  message_received: { icon: MessageCircle },
  mention: { icon: AtSign },
  system: { icon: Info },
};

export default NotificationsPage;
